<template>
  <div class="edit-submission">
    <div class="loading-pane" :class="{ active: busy, 'post-note': !!result }">
      <div class="spin" />
      <div class="message">&nbsp;{{ result }}&nbsp;</div>
    </div>
    <div class="edit-form">
      <div
        v-if="submission.template === 'mtga'"
        is="EditMTGADeck"
        v-model="details" />
      <div
        v-else-if="submission.template === 'generic'"
        is="EditGeneric"
        v-model="details" />
      <h3>Lane</h3>
      <label>
        <select
          v-model="laneId"
          @change="upsellIds = [], productId = ''">
          <option
            v-for="lane of lanes"
            :key="lane.id"
            :value="lane.id"
            v-text="lane.title"
            :disabled="lane.template !== submission.template" />
        </select>
      </label>
      <div v-if="selectedLane">
        <h3>Type</h3>
        <label>
          <select v-model="productId">
            <option
              v-for="product of selectedLane.products"
              :key="product.key"
              :value="product.key">
              {{ product.label }}
            </option>
          </select>
        </label>
      </div>
      <div v-if="selectedLane && selectedLane.upsells.length > 0">
        <h3>Upsells</h3>
        <div v-for="upsell of selectedLane.upsells" :key="upsell.key">
          <label>
            <input type="checkbox" v-model="upsellIds" :value="upsell.key" />
            {{ upsell.label }}
          </label>
        </div>
      </div>
      <br />
      <br />
      <button @click="updateSubmission" :disabled="busy">Save changes</button>
      <div v-if="result">{{ result }}</div>
    </div>
    <div class="meta-page">
      <h3>Metadata</h3>
      <div class="meta">
        <div>
          <strong>Created:</strong>
          {{ submission.created.toDate() | moment('MMMM Do YYYY, h:mm:ss a') }}
          by <router-link :to="`/users/${submission.authorId}`">{{ submission.authorName }}</router-link>
        </div>
        <div>
          <strong>Status:</strong>
          <span class="icon emoji" :class="getStatus(submission)"></span>
          {{ getStatus(submission) }}
        </div>
        <div>
          <strong>Score:</strong>
          {{ getScore(submission) }}
          <small>
            +{{ getScoreAge(submission) }} from age
            +{{ getScoreBumps(submission) }} from bumps
          </small>
        </div>
        <div v-if="submission.status === 'approved' && submission.approvedTime">
          <strong>Approved:</strong>
          {{ submission.approvedTime.toDate() | moment('MMMM Do YYYY, h:mm:ss a') }}
          by
          <strong>{{ submission.approver }}</strong>
        </div>
        <div v-if="submission.archived && submission.archivalDate">
          <strong>Archived:</strong>
          {{ submission.archivalDate.toDate() | moment('MMMM Do YYYY, h:mm:ss a')}}
        </div>
        <hr />
        <div>
          <strong>Adjustments:</strong>
          <div
            v-for="adjustment in (submission.adjustments || [])"
            :key="adjustment.transactionId">
            <template v-if="adjustment.created">
              {{ adjustment.created.toDate() | moment('MMMM Do YYYY, h:mm:ss a') }}
              &ndash;
            </template>
            {{ adjustment.amount }}
          </div>
        </div>
        <hr />
        <div>
          <h3>Delete</h3>
          <label>
            Refund all points:
            <input type="checkbox" v-model="deleteWithRefund" @change="resetDeleteReasonBody" />
            <small class="warning" v-if="!deleteWithRefund">
              <i class="icon fas fa-exclamation-triangle"></i>
              I hope you know what you're doing dude
            </small>
          </label>
          <br />
          <label>
            Include response message:
            <input type="checkbox" v-model="deleteWithMessage" />
          </label>
          <br />
          <fieldset v-if="deleteWithMessage">
            <legend>Response message</legend>

            <strong>Subject:</strong>
            <br />
            <input type="text" v-model="deleteReasonSubject" size="50" />
            <br /><br />
            <strong>Message:</strong>
            <br />
            <textarea
              v-model="deleteReasonBody"
              rows="7"
              cols="50"
              @change="deleteReasonDirty = true" />
          </fieldset>
          <button
            @click="deleteSubmission()"
            :disabled="busy">Delete submission</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ApiHelper from '../mixins/ApiHelper'
import DeckHelper from '../mixins/DeckHelper'
import { DECK_STATUS } from '../constants'

import EditMTGADeck from '@/components/templates/MTGA/Edit'
import EditGeneric from '@/components/templates/Generic/Edit'

export default {
  name: 'EditSubmission',
  props: ['id'],
  mixins: [ApiHelper, DeckHelper],
  components: {
    EditMTGADeck,
    EditGeneric
  },

  data () {
    return {
      details: {},
      laneId: '',
      upsellIds: [],
      productId: '',
      submission: {},

      deleteReasonSubject: '',
      deleteReasonBody: 'Your submission has been rejected.',
      deleteReasonDirty: false,
      deleteWithMessage: true,
      deleteWithRefund: true,

      result: '',
      busy: false
    }
  },

  computed: {
    ...mapState(['lanes']),
    selectedLane () {
      return this.lanes.find((l) => l.id === this.laneId)
    }
  },

  methods: {
    updateSubmission () {
      const { details, upsellIds, productId, laneId } = this
      this.busy = true
      this.result = 'Updating'
      this.apiRequest(`submission/${this.id}`, {
        method: 'patch',
        data: {
          details,
          lane: laneId,
          upsells: upsellIds,
          product: productId
        }
      })
      .then(() => this.result = '')
      .catch((err) => this.result = err.toString())
      .finally(() => this.busy = false)
    },

    deleteSubmission () {
      if (!confirm('Are you sure you want to permanently delete this submission? This is irreversible.')) {
        return
      }

      this.busy = true
      this.result = 'Deleting'

      const authorId = this.submission.authorId
      const refund = this.deleteWithRefund
      this.apiRequest(`submission/${this.submission.id}`, {
        method: 'delete',
        data: { refund }
      })
      .then(() => {
        this.result = 'Sending mail'
        return this.apiRequest('mail', {
          method: 'post',
          data: {
            to: authorId,
            subject: this.deleteReasonSubject,
            message: this.deleteReasonBody
          }
        })
      })
      .then(() => {
        this.result = ''
        // this.$router.go(-1)
      })
      .catch((err) => {
        this.result = err.toString()
      })
      .finally(() => this.busy = false)
    },

    resetDeleteReasonBody () {
      if (this.deleteReasonDirty) {
        return
      }

      let body = ''
      if (this.deleteWithRefund) {
        body += 'Your submission has been rejected and all brew points have been refunded. Feel free to resubmit it after making the appropriate changes.'
      } else {
        body += 'Your submission has been deleted.'
      }
      this.deleteReasonBody = body
    }
  },

  created () {
    const submission = this.$store.getters.getSubmission(this.id)
    this.details = {
      ...submission.details
    }
    this.upsellIds = (submission.upsells || []).map((u) => u.key)
    this.productId = submission.product && submission.product.key
    this.laneId = submission.lane
    this.submission = submission

    const status = this.getStatus(submission)
    switch (status) {
      case DECK_STATUS.PENDING:
      case DECK_STATUS.APPROVED:
        this.deleteWithRefund = true
        this.deleteWithMessage = true
        break
      case DECK_STATUS.ARCHIVED:
        this.deleteWithRefund = false
        this.deleteWithMessage = false
        break
    }
    this.deleteReasonSubject = `Rejected: "${submission.title}"`
    this.resetDeleteReasonBody()
  }
}
</script>

<style scoped>
.edit-submission {
  display: flex;
}
.edit-submission.busy {
  opacity: 0.5;
  cursor: wait;
  pointer-events: none;
}
.meta-page {
  margin-left: 1em;
  padding-left: 1em;
  border-left: 1px solid #ccc;
}
.edit-form h3 {
  margin: 1em 0 0;
}
.warning { color: orangered; }

.loading-pane {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background-color: white;
  opacity: 0;
  transition: opacity 100ms linear;
  transition-delay: 0s;
}
.loading-pane.post-note {
  transition-delay: 3s;
}
.loading-pane.active {
  pointer-events: all;
  opacity: 0.9;
  transition-delay: 0s;
}
.loading-pane .message {
  margin-top: 2em;
  font-size: larger;
}
.loading-pane .spin {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 5px solid #ccc;
  border-top-color: dodgerblue;
  animation: spin 2s infinite linear;
}
@keyframes spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
